import React, { useEffect, useRef, useState } from "react";
import { useSocket } from "./Contexts/SocketContext";
import { useStreamInput } from "../hooks/useStreamInput";
import {
  BiSignal5,
  BiSignal3,
  BiSignal1,
  BiNetworkChart,
  BiWifi,
  BiWifiOff,
} from "react-icons/bi";
import { BsArrowsAngleExpand, BsPencil, BsXCircle } from "react-icons/bs";
import Spinner from "react-bootstrap/Spinner";

interface SystemStreamPaneProps {
  systemId: number;
  systemName: string;
  patientName: string;
  networkType: string;
  ping: number;
  organizationId: string;
  // When true, this pane is the controlled system in MultiViewModal —
  // mouse and keyboard events on it are sent to the remote system via
  // the shared useStreamInput hook. View-only otherwise.
  controlling?: boolean;
  onTakeControl: (systemId: number) => void;
  // Called when the operator clicks Release on a controlled pane. If
  // omitted, the Release button is hidden — useful for callers that
  // don't support the controlling mode.
  onReleaseControl?: (systemId: number) => void;
}

const STUN_SERVERS: RTCIceServer[] = [
  { urls: "stun:turn.epicarealert.com:3478" },
];

// View-only WebRTC cell. Self-contained: owns its peer connection, socket
// listeners (filtered by systemId so multiple panes don't fight over the
// same events), video display, and Add Note popup. Used by MultiViewModal
// in a 2x2 grid. No input pipeline — operator monitors but doesn't control.
// "Take Control" defers to the parent, which is expected to swap this
// cell out for the full LiveStreamModal on that system.
const SystemStreamPane: React.FC<SystemStreamPaneProps> = ({
  systemId,
  systemName,
  patientName,
  networkType,
  ping,
  organizationId,
  controlling = false,
  onTakeControl,
  onReleaseControl,
}) => {
  const { socket } = useSocket();
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const iceServersRef = useRef<RTCIceServer[]>(STUN_SERVERS);
  const forceRelayRef = useRef(false);

  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showNotePopup, setShowNotePopup] = useState(false);
  const [noteText, setNoteText] = useState("");

  const hasStudy = !!patientName?.trim();
  const sysIdStr = String(systemId);

  // Input pipeline (mouse/keyboard/wheel → remote system) is gated by
  // `controlling`. When false, the hook is a no-op and we behave as a
  // pure view-only pane. The hook also owns the input data channel,
  // which we hand it via bindInputChannel from pc.ondatachannel below.
  const {
    handleMouseMove,
    handleMouseDown,
    handleMouseUp,
    handleKeyDown,
    bindInputChannel,
  } = useStreamInput({
    socket,
    systemId,
    videoRef,
    videoWrapperRef,
    enabled: controlling,
  });

  // When the operator promotes this pane to controlled, focus the wrapper
  // so subsequent keystrokes flow to handleKeyDown without an extra
  // click. tabIndex={0} on the wrapper makes it focusable.
  useEffect(() => {
    if (controlling) {
      videoWrapperRef.current?.focus();
    }
  }, [controlling]);

  // Network/signal indicator — same color/icon rules as LiveStreamModal so
  // operators can compare panes at a glance.
  const renderConnectionIcon = () => {
    const color = ping < 150 ? "#10b981" : ping < 300 ? "#f59e0b" : "#ef4444";
    if (networkType === "cellular") {
      if (ping < 150) return <BiSignal5 style={{ color }} />;
      if (ping < 300) return <BiSignal3 style={{ color }} />;
      return <BiSignal1 style={{ color }} />;
    }
    if (networkType === "ethernet") return <BiNetworkChart style={{ color }} />;
    return ping < 500 ? <BiWifi style={{ color }} /> : <BiWifiOff style={{ color: "#ef4444" }} />;
  };

  const submitNote = () => {
    if (!socket) return;
    socket.emit("wn", { systemId, note: noteText.trim() });
    setShowNotePopup(false);
    setNoteText("");
  };

  useEffect(() => {
    if (!socket) return;

    // All operator-bound stream events now include systemId from backend,
    // so we filter to only the ones for our pane.
    const onTurn = ({ username, credential, forceRelay, local, systemId: sid }: any) => {
      if (String(sid) !== sysIdStr) return;
      forceRelayRef.current = !!forceRelay;
      // Same-browser peer (sandbox): no ICE servers needed. See LiveStreamModal.
      if (local) {
        iceServersRef.current = [];
        return;
      }
      iceServersRef.current = [
        ...STUN_SERVERS,
        { urls: "turns:turn.epicarealert.com:443?transport=tcp", username, credential },
        { urls: "turns:turn.epicarealert.com:5349?transport=tcp", username, credential },
        { urls: "turn:turn.epicarealert.com:3478?transport=tcp", username, credential },
        { urls: "turn:turn.epicarealert.com:3478", username, credential },
      ];
    };

    const onOffer = async ({ offer, systemId: sid }: any) => {
      if (String(sid) !== sysIdStr) return;
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      setConnected(false);
      setError(null);

      const pcConfig: RTCConfiguration = { iceServers: iceServersRef.current };
      if (forceRelayRef.current) pcConfig.iceTransportPolicy = "relay";
      const pc = new RTCPeerConnection(pcConfig);
      pcRef.current = pc;

      pc.ontrack = ({ streams }) => {
        if (videoRef.current && streams[0]) {
          videoRef.current.srcObject = streams[0];
          setConnected(true);
        }
      };

      // System always creates the input data channel — bind it now so the
      // moment this pane is promoted to controlling, sends go P2P. When
      // not controlling, the hook ignores the channel (enabled flag).
      pc.ondatachannel = (event) => {
        if (event.channel.label !== "input") return;
        bindInputChannel(event.channel);
      };

      pc.onicecandidate = ({ candidate }) => {
        if (candidate) {
          socket.emit("STREAM_ICE_FROM_FRONTEND", {
            candidate: candidate.toJSON(),
            systemId,
          });
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === "failed") {
          setError("Connection failed");
          setConnected(false);
        }
      };

      try {
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit("STREAM_ANSWER", {
          answer: { type: answer.type, sdp: answer.sdp },
          systemId,
        });
      } catch (e) {
        setError("Failed to establish stream");
      }
    };

    const onIce = async ({ candidate, systemId: sid }: any) => {
      if (String(sid) !== sysIdStr) return;
      if (pcRef.current && candidate) {
        try {
          await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (_) {}
      }
    };

    socket.on("TURN_CREDENTIALS", onTurn);
    socket.on("STREAM_OFFER", onOffer);
    socket.on("STREAM_ICE_FROM_SYSTEM", onIce);

    // Kick off the request — backend sends TURN_CREDENTIALS to us, then
    // forwards START_STREAM to the system app, which generates an offer.
    socket.emit("REQUEST_STREAM", { systemId, organizationId });

    return () => {
      socket.off("TURN_CREDENTIALS", onTurn);
      socket.off("STREAM_OFFER", onOffer);
      socket.off("STREAM_ICE_FROM_SYSTEM", onIce);
      bindInputChannel(null);
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      socket.emit("STOP_STREAM", { systemId });
    };
  }, [socket, systemId, organizationId, sysIdStr, bindInputChannel]);

  return (
    <div
      style={{
        position: "relative",
        background: "#0f172a",
        borderRadius: "10px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        // flex:1 lets the pane fill flex-container parents (MultiViewModal
        // wraps each in a flex cell for grid-area positioning). The grid
        // cell itself sizes the wrapper; this just makes the pane fill it.
        flex: 1,
        minWidth: 0,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: "rgba(15, 23, 42, 0.95)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          color: "#f8fafc",
          fontSize: "13px",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
          <span style={{ flexShrink: 0 }}>{renderConnectionIcon()}</span>
          <span style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {hasStudy ? patientName : systemName}
          </span>
          {hasStudy && (
            <span style={{ color: "#94a3b8", fontSize: "11px", whiteSpace: "nowrap" }}>
              · {systemName}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
          <button
            onClick={() => setShowNotePopup(true)}
            disabled={!connected || !hasStudy}
            title={!hasStudy ? "No active study" : !connected ? "Connecting..." : "Add note to study"}
            style={{
              background: connected && hasStudy ? "#2563eb" : "#1e3a8a",
              border: "none",
              borderRadius: "6px",
              color: "white",
              padding: "5px 10px",
              cursor: connected && hasStudy ? "pointer" : "not-allowed",
              fontWeight: 600,
              fontSize: "11px",
              opacity: connected && hasStudy ? 1 : 0.6,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <BsPencil size={11} /> Note
          </button>
          {controlling && onReleaseControl ? (
            <button
              onClick={() => onReleaseControl(systemId)}
              title="Release control — back to view-only"
              style={{
                background: "#dc2626",
                border: "none",
                borderRadius: "6px",
                color: "white",
                padding: "5px 10px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "11px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <BsXCircle size={11} /> Release
            </button>
          ) : (
            <button
              onClick={() => onTakeControl(systemId)}
              disabled={!connected}
              title="Take control — enable mouse/keyboard on this system"
              style={{
                background: connected ? "#7c3aed" : "#4c1d95",
                border: "none",
                borderRadius: "6px",
                color: "white",
                padding: "5px 10px",
                cursor: connected ? "pointer" : "not-allowed",
                fontWeight: 600,
                fontSize: "11px",
                opacity: connected ? 1 : 0.6,
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <BsArrowsAngleExpand size={11} /> Control
            </button>
          )}
        </div>
      </div>

      {/* Video */}
      <div
        ref={videoWrapperRef}
        tabIndex={controlling ? 0 : -1}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onKeyDown={handleKeyDown}
        onContextMenu={(e) => { if (controlling) e.preventDefault(); }}
        style={{
          flex: 1,
          position: "relative",
          background: "#000",
          minHeight: 0,
          outline: "none",
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          onMouseMove={handleMouseMove}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            // Custom local cursor SVG when controlling — drawn by the
            // browser at the OS cursor position so it feels instant
            // regardless of network latency. Same arrow as the single-
            // system live view so the operator's experience is identical.
            cursor: controlling
              ? "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='24' viewBox='0 0 20 24'><path d='M1 1 L1 17 L5 13 L8 20 L10 19 L7 12 L13 12 Z' fill='white' stroke='black' stroke-width='1.3' stroke-linejoin='round'/></svg>\") 1 1, auto"
              : "default",
          }}
        />
        {!connected && !error && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              color: "#94a3b8",
              gap: "10px",
              fontSize: "12px",
            }}
          >
            <Spinner animation="border" size="sm" />
            Connecting...
          </div>
        )}
        {error && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ef4444",
              fontSize: "12px",
              padding: "12px",
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}

        {/* Note popup overlays the video for this cell only */}
        {showNotePopup && (
          <div
            onClick={() => setShowNotePopup(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15, 23, 42, 0.75)",
              backdropFilter: "blur(4px)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#1e293b",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "10px",
                padding: "14px",
                width: "100%",
                maxWidth: "360px",
              }}
            >
              <div style={{ color: "#f8fafc", fontSize: "13px", fontWeight: 600, marginBottom: "8px" }}>
                Add note · {patientName || systemName}
              </div>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submitNote();
                  if (e.key === "Escape") setShowNotePopup(false);
                }}
                autoFocus
                placeholder="Leave blank for &quot;Checked by [you]&quot;"
                style={{
                  width: "100%",
                  minHeight: "70px",
                  background: "#0f172a",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "6px",
                  padding: "8px",
                  color: "#f8fafc",
                  fontSize: "12px",
                  resize: "vertical",
                  outline: "none",
                  marginBottom: "10px",
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                <button
                  onClick={() => setShowNotePopup(false)}
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: "6px",
                    color: "#cbd5e1",
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitNote}
                  style={{
                    background: "#2563eb",
                    border: "none",
                    borderRadius: "6px",
                    color: "white",
                    padding: "6px 14px",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  Add Note
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemStreamPane;
